define('presentation/reactivation/viewModels/reactivationPlansViewModel',[
    'businessServices/authentication/authenticationManager',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/authentication/sessionWorkflowFlags',
    'businessServices/login/accountStatusEvaluator',
    'common/promises/promiseFactory',
    'common/url/urlFormatter',
    'constants/billingMethodConstants',
    'constants/billingPackageTypeConstants',
    'constants/creditCardResponseConstants',
    'plugins/router',
    'presentation/common/window/windowControl',
    'presentation/reactivation/facades/reactivationPlansFacade',
    'presentation/reactivation/presentationObjects/planPresentationObject',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const PlanPresentationObjectConstructor = require('presentation/reactivation/presentationObjects/planPresentationObject');

        const _i18n = require('i18next');
        const _accountStatusEvaluator = require('businessServices/login/accountStatusEvaluator');
        const _authenticationManager = require('businessServices/authentication/authenticationManager');
        const _billingMethodConstants = require('constants/billingMethodConstants');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');
        const _creditCardResponse = require('constants/creditCardResponseConstants');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('plugins/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');
        const _sessionWorkflowFlags = require('businessServices/authentication/sessionWorkflowFlags');
        const _urlFormatter = require('common/url/urlFormatter');
        const _windowControl = require('presentation/common/window/windowControl');

        let _facade = null;

        const _buildEmptyPlanPresentationObject = (type) => {
            const planPresentationObject = new PlanPresentationObjectConstructor();
            planPresentationObject.name(_i18n.t('plans:labelNone'));
            planPresentationObject.type(type);
            return planPresentationObject;
        };

        const _buildPlanPresentationObject = (billingPackage, isSignatureService) => {
            const planPresentationObject = new PlanPresentationObjectConstructor();
            planPresentationObject.billingPackageId(billingPackage.billingPackageId);
            planPresentationObject.packageId(billingPackage.packageId);
            planPresentationObject.type(billingPackage.type);
            planPresentationObject.name(billingPackage.name);
            planPresentationObject.isSignatureService(isSignatureService);
            return planPresentationObject;
        };

        const _preparePlanForDisplay = (billingPackage, type, isPackageTypeAvailable, isSignatureService) => {
            return billingPackage ?
                _buildPlanPresentationObject(billingPackage, isSignatureService) :
                isPackageTypeAvailable ? _buildEmptyPlanPresentationObject(type) : null;
        };

        const _getAccountPlan = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getAccountPlan()
                    .fail(promise.reject)
                    .done((accountPlan) => {
                        const {
                            planId,
                            telephonyPackage,
                            liveReceptionistPackage,
                            isLiveReceptionistSignatureService,
                            isLiveReceptionistAvailable,
                            webChatPackage,
                            isWebChatAvailable,
                            outboundCallingPackage,
                            isOutboundCallingAvailable,
                        } = accountPlan;

                        const displayTelephonyPackage = _preparePlanForDisplay(telephonyPackage, _billingPackageTypes.telephony.id, true, false);
                        const displayLiveReceptionistPackage = _preparePlanForDisplay(liveReceptionistPackage, _billingPackageTypes.liveReceptionist.id, isLiveReceptionistAvailable, isLiveReceptionistSignatureService);
                        const displayWebChatPackage = _preparePlanForDisplay(webChatPackage, _billingPackageTypes.webChat.id, isWebChatAvailable, false);
                        const displayOutboundCallingPackage = _preparePlanForDisplay(outboundCallingPackage, _billingPackageTypes.outboundCalling.id, isOutboundCallingAvailable, false);

                        let displayPlans = [];
                        if (displayLiveReceptionistPackage !== null)
                        {
                            displayPlans.push(displayLiveReceptionistPackage);
                        }
                        if (displayWebChatPackage !== null)
                        {
                            displayPlans.push(displayWebChatPackage);
                        }
                        if (displayOutboundCallingPackage !== null)
                        {
                            displayPlans.push(displayOutboundCallingPackage);
                        }

                        const hasFlexPlan = liveReceptionistPackage === null && webChatPackage === null && outboundCallingPackage === null;
                        if (hasFlexPlan) {
                            displayPlans.unshift(displayTelephonyPackage);
                        }
                        self.planId(planId);
                        self.telephonyPackage(telephonyPackage);
                        self.selectedPlans(displayPlans);

                        promise.resolve();
                    });
            });
        };

        const _removeSelectedPlan = (packageType) => {
            const currentPackage = self.selectedPlans().find((plan) => plan.type() === packageType);
            if (currentPackage) {
                currentPackage.billingPackageId(null);
                currentPackage.packageId("");
                currentPackage.name(_i18n.t('plans:labelNone'));
            }
        };

        const _updateSelectedPlan = (selectedPackage) => {
            const currentPackage = self.selectedPlans().find((plan) => plan.type() === selectedPackage.type);
            if (currentPackage) {
                currentPackage.billingPackageId(selectedPackage.billingPackageId);
                currentPackage.packageId(selectedPackage.packageId);
                currentPackage.name(selectedPackage.name);
            }
        };

        const _getAvailablePackages = (packageType) => {
            return _promiseFactory.defer((promise) => {
                _facade.getBillingPackages(packageType)
                    .fail(promise.reject)
                    .done((billingPackages) => {
                        promise.resolve(billingPackages);
                    });
            });
        };

        const _getDefaultPaymentMethod = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getDefaultPaymentMethod()
                    .fail(promise.reject)
                    .done((defaultPaymentMethod) => {
                        promise.resolve(defaultPaymentMethod);
                    });
            });
        };

        const _getReactivationInformation = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getReactivationInformation()
                    .fail(promise.reject)
                    .done((reactivationInformation) => {
                        self.billingMethod(reactivationInformation.billingMethod || _billingMethodConstants.CreditCard);
                        promise.resolve();
                    });
            });
        };

        const _getPaymentMethodForReactivation = () => {
            return _promiseFactory.defer((promise) => {
                if (self.isBilledByCreditCard() === false) {
                    promise.resolve(null);
                    return;
                }
                _getDefaultPaymentMethod()
                    .fail(promise.reject)
                    .done(promise.resolve);
            });
        };

        const _reactivateAccount = () => {
            return _promiseFactory.deferIndefinitely((deferredObject) => {
                _getPaymentMethodForReactivation()
                    .fail(deferredObject.reject)
                    .done((defaultPaymentMethod) => {
                        const reactivationData = {
                            planId: self.planId(),
                            telephonyPackageId: self.telephonyPackageId(),
                            liveReceptionistPackageId: self.liveReceptionistPackageId(),
                            webChatPackageId: self.webChatPackageId(),
                            outboundCallingPackageId: self.outboundCallingPackageId(),
                            paymentMethodId: defaultPaymentMethod ? defaultPaymentMethod.paymentMethodId : null,
                            newPaymentMethod: defaultPaymentMethod
                        };
                        _facade.reactivateAccount(reactivationData)
                            .fail(deferredObject.reject)
                            .done((reactivationResult) => {
                                if (_validatePaymentProcessing(reactivationResult.status)) {
                                    deferredObject.resolve(true);
                                } else {
                                    self.showReactivationFailureMessage(true);
                                    deferredObject.resolve(false);
                                }
                            });
                    });
            });
        };

        const _validatePaymentProcessing = (paymentStatus) => {
            switch (paymentStatus) {
                case _creditCardResponse.success:
                case _creditCardResponse.approved:
                    self.paymentFailureTitle('');
                    self.paymentFailureMessage('');
                    return true;
                case _creditCardResponse.declined:
                    self.paymentFailureTitle(_i18n.t('reactivation:declinedTitle'));
                    self.paymentFailureMessage(_i18n.t('reactivation:declinedMessage'));
                    return false;
                case _creditCardResponse.duplicateCard:
                case _creditCardResponse.invalidCard:
                case _creditCardResponse.invalidConnection:
                    self.paymentFailureTitle(_i18n.t('reactivation:genericFailureTitle'));
                    self.paymentFailureMessage(_i18n.t('reactivation:genericFailureMessage'));
                    return false;
                default:
                    throw new Error(paymentStatus);
            }
        };

        const _togglePackagePicker = () => {
            self.showPackagePicker(!self.showPackagePicker());
        };

        self.pricingUrl = _urlFormatter.buildFrontendUrl(["pricing"]);
        self.isCompositionComplete = ko.observable(false);
        self.isSubmitInProgress = ko.observable(false);
        self.isAccountReinstatement = ko.observable(false);
        self.planId = ko.observable(null);
        self.telephonyPackage = ko.observable(null);
        self.selectedPlans = ko.observableArray([]);
        self.packagePickerAvailablePackages = ko.observableArray([]);
        self.packagePickerPackageId = ko.observable(null);
        self.packagePickerPackageType = ko.observable(null);
        self.showReactivationFailureMessage = ko.observable(false);
        self.paymentFailureTitle = ko.observable('');
        self.paymentFailureMessage = ko.observable('');
        self.showPackagePicker = ko.observable(false);
        self.billingMethod = ko.observable(_billingMethodConstants.CreditCard);

        self.isBilledByCreditCard = ko.pureComputed(() => self.billingMethod() === _billingMethodConstants.CreditCard);

        self.shouldDisplayAgentBypassButton = ko.pureComputed(() => {
            return _sessionAccountInfo.isImpersonating();
        });

        self.headerTitle = ko.pureComputed(() => {
            if (self.showPackagePicker()) {
                switch (self.packagePickerPackageType()) {
                    case _billingPackageTypes.liveReceptionist.id:
                        return _i18n.t("reactivationPlans:packagePickerTitle", { type: _billingPackageTypes.liveReceptionist.name});
                    case _billingPackageTypes.webChat.id:
                        return _i18n.t("reactivationPlans:packagePickerTitle", { type: _billingPackageTypes.webChat.name});
                    case _billingPackageTypes.outboundCalling.id:
                        return _i18n.t("reactivationPlans:packagePickerTitle", { type: _billingPackageTypes.outboundCalling.name});
                    default:
                        return "";
                }
            } else {
                return _i18n.t("reactivationPlans:headerTitle");
            }
        });

        self.headerMessage = ko.pureComputed(() => {
            if (self.showPackagePicker()) {
                switch (self.packagePickerPackageType()) {
                    case _billingPackageTypes.liveReceptionist.id:
                        return _i18n.t("reactivationPlans:packagePickerMessage", {context: _billingPackageTypes.liveReceptionist.key});
                    case _billingPackageTypes.webChat.id:
                        return _i18n.t("reactivationPlans:packagePickerMessage", {context: _billingPackageTypes.webChat.key});
                    case _billingPackageTypes.outboundCalling.id:
                        return _i18n.t("reactivationPlans:packagePickerMessage", {context: _billingPackageTypes.outboundCalling.key});
                    default:
                        return "";
                }
            } else {
                return _i18n.t("reactivationPlans:headerMessage");
            }
        });

        self.telephonyPackageId = ko.pureComputed(() => {
            const telephonyPackage = self.selectedPlans().find((plan) => plan.type() === _billingPackageTypes.telephony.id);
            return telephonyPackage ? telephonyPackage.packageId() : self.telephonyPackage().packageId;
        });

        self.liveReceptionistPackageId = ko.pureComputed(() => {
            const liveReceptionistPackage = self.selectedPlans().find((plan) => plan.type() === _billingPackageTypes.liveReceptionist.id);
            return liveReceptionistPackage ? liveReceptionistPackage.packageId() : null;
        });

        self.webChatPackageId = ko.pureComputed(() => {
            const webChatPackage = self.selectedPlans().find((plan) => plan.type() === _billingPackageTypes.webChat.id);
            return webChatPackage ? webChatPackage.packageId() : null;
        });

        self.outboundCallingPackageId = ko.pureComputed(() => {
            const outboundCallingPackage = self.selectedPlans().find((plan) => plan.type() === _billingPackageTypes.outboundCalling.id);
            return outboundCallingPackage ? outboundCallingPackage.packageId() : null;
        });

        self.onEditPlan = (planPresentationObject) => {
            _getAvailablePackages(planPresentationObject.type())
                .done((availablePackages) => {
                    self.packagePickerAvailablePackages(availablePackages);
                    switch (planPresentationObject.type()) {
                        case _billingPackageTypes.liveReceptionist.id:
                        case _billingPackageTypes.webChat.id:
                        case _billingPackageTypes.outboundCalling.id:
                            self.packagePickerPackageId(planPresentationObject.packageId());
                            self.packagePickerPackageType(planPresentationObject.type());
                            _togglePackagePicker();
                            break;
                        default:
                            self.packagePickerPackageId(null);
                            self.packagePickerPackageType(null);
                    }
                });
        };

        self.onPackagePickerSelect = (packagePickerPresentationObject) => {
            _updateSelectedPlan(packagePickerPresentationObject);
            _togglePackagePicker();
        };

        self.onRemovePlan = (planPresentationObject) => {
            _removeSelectedPlan(planPresentationObject.type());
        };

        self.onBack = () => {
            _togglePackagePicker();
        };

        self.onCancel = () => {
            if (self.isSubmitInProgress()) {
                return;
            }

            _authenticationManager.logout()
                .done(() => {
                    _router.navigate(_navigationConfiguration.loginPageUrl);
                });
        };

        self.openTermsOfAgreement = () => {
            _windowControl.openTermsOfAgreement();
        };

        self.onAgentBypass = () => {
            if (self.isSubmitInProgress()) {
                return;
            }

            _sessionWorkflowFlags.hasBypassedClosedAccount(true);
            _sessionWorkflowFlags.hasBypassedPastDueBalance(true);
            _router.navigate(_navigationConfiguration.loginLandingPageUrl);
        };

        self.reactivateAccount = () => {
            self.showReactivationFailureMessage(false);
            return _promiseFactory.deferWithMinimumWait((deferredObject) => {
                _reactivateAccount()
                    .fail(deferredObject.reject)
                    .done((reactivationSuccessful) => {
                        if (reactivationSuccessful) {
                            _accountStatusEvaluator.refresh();
                            _router.navigate(_navigationConfiguration.routesById.reactivationSuccess.url);
                            deferredObject.resolve();
                        } else {
                            self.showReactivationFailureMessage(true);
                            deferredObject.resolve();
                        }
                    });
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/reactivation/facades/reactivationPlansFacade');
            _facade = new FacadeConstructor();
            _facade.init();

            return _initialize();
        };

        const _initialize = () => {
            _getAccountPlan();
            _getReactivationInformation();

            return _promiseFactory.wait();
        };
    };
});
